import { Unauthenticated, Authenticated } from '@/utils/auth/auth'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { Link } from 'react-router-dom'

import { UUID } from 'crypto'
import LoginSignup from '@/components/LoginSignup'
import { getEffectiveOrgUuid } from '@/utils/utils'
import { useApplicationsSummary } from '@/utils/hooks/applications'
import { useEffect, useState } from 'react'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import Loading from '@/components/Loading'
import ErrorMessage from '@/components/ErrorMessage'
import { useFundingNeeds } from '@/utils/hooks/fundingNeeds'
import { usePageTitle } from '@/utils/hooks/usePageTitle'

export default function ApplicationsNeedListPage() {
  usePageTitle('Applications')

  const [showArchived, setShowArchived] = useState(false)
  const [hasArcivedNeeds, setHasArchivedNeeds] = useState(false)
  const focusRef = useFocusManagement()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()

  if (!userLoading && orgs && orgs.length === 0) {
    window.location.href = '/'
  }

  const {
    orgData,
    orgErrors,
    isLoading: orgsLoading,
    isError,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])

  const effectiveOrgUuid = getEffectiveOrgUuid(user, orgs)
  const organisation = orgData.find((org) => org.uuid === effectiveOrgUuid)

  const {
    data: applicationsSummary,
    isLoading: applicationsLoading,
    error,
  } = useApplicationsSummary(token as string, effectiveOrgUuid)

  const {
    data: fundingNeeds,
    isLoading: fundingNeedsLoading,
    error: fundingNeedsError,
  } = useFundingNeeds(token, organisation?.uuid as UUID)

  useEffect(() => {
    if (!applicationsSummary) return

    setHasArchivedNeeds(
      applicationsSummary.some(
        (summary) => summary.funding_need_is_archived === true,
      ),
    )
  }, [applicationsSummary])

  if (
    userLoading ||
    orgsLoading ||
    applicationsLoading ||
    fundingNeedsLoading
  ) {
    return <Loading />
  }

  if (isError || error || orgErrors.length > 0 || fundingNeedsError) {
    return <ErrorMessage />
  }

  // Filter active and archived application summaries
  const activeApplicationSummaries =
    applicationsSummary?.filter(
      (summary) => !summary.funding_need_is_archived,
    ) || []

  const archivedApplicationSummaries =
    applicationsSummary?.filter(
      (summary) => summary.funding_need_is_archived,
    ) || []

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <>
          {!user || !token || (!organisation && <ErrorMessage />)}

          {user && token && organisation && (
            <Box
              sx={{
                margin: '0 auto',
                marginTop: 2,
                width: '100%',
                pb: 5,
                px: { xs: 2, md: 15 },
                mb: 5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'space-between',
                  flexDirection: 'column',
                  mb: 4,
                }}
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                  width="100%"
                >
                  <Typography
                    variant="h1"
                    component="h1"
                    ref={focusRef}
                    tabIndex={-1}
                    sx={{
                      mb: 2,
                      textAlign: 'left',
                      fontSize: '34px',
                      '&:focus': {
                        outline: 'none',
                      },
                    }}
                  >
                    Funding Request Applications
                  </Typography>

                  {/* IMPLEMENT LATER - SHOW APPLICATION TRACKING */}
                  {/*
                        <Button
                        component{Link}
                        to="/tracking"
                          disableRipple
                          variant="outlined"
                          sx={{
                            textTransform: 'none',
                            color: '#1C3045',
                            borderColor: '#1C3045',
                            mr: 6,
                          }}
                        >
                          View Application Tracking
                        </Button>
                     */}
                </Box>

                {hasArcivedNeeds && (
                  <Box sx={{ alignSelf: 'flex-start' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showArchived}
                          onChange={(e) => setShowArchived(e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#1C3045',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                              {
                                backgroundColor: '#1C3045',
                              },
                            '& .MuiSwitch-thumb': {
                              border: showArchived
                                ? '1px solid #1C3045'
                                : '1px solid #666666',
                            },
                          }}
                        />
                      }
                      label="Show Archived"
                    />
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                {organisation && fundingNeeds && fundingNeeds.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: isMobile ? '100%' : '60vw',
                      px: isMobile ? 0 : 6,
                      mt: 10,
                      height: '20vh',
                      border: 1,
                      borderColor: '#1C3045',
                      borderRadius: 2,
                      mx: 'auto',
                      bgcolor: '#f7f7f7',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 1,
                        fontSize: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {' '}
                      You don't have any Funding Requests yet!
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        mb: 1,
                        textAlign: 'center',
                        fontSize: '16px',
                      }}
                    >
                      {' '}
                      Add a Funding Request on the{' '}
                      <Link to="/" style={{ color: '#1C3045' }}>
                        Funding Requests Page{' '}
                      </Link>{' '}
                      to get started
                    </Typography>
                  </Box>
                ) : activeApplicationSummaries.length > 0 ? (
                  isMobile ? (
                    <List
                      sx={{
                        width: '100%',
                        bgcolor: 'background.paper',
                        position: 'relative',
                        overflow: 'auto',
                        maxHeight: 600,
                        '& ul': { padding: 2 },
                      }}
                    >
                      {activeApplicationSummaries.map((summary) => {
                        return (
                          <Box
                            key={summary.need_id}
                            sx={{
                              width: '100%',
                            }}
                          >
                            <Divider />
                            <ListItem
                              sx={{
                                py: 1,
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      my: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: '16px',
                                        textAlign: 'center',
                                        mb: 1,
                                      }}
                                    >
                                      {summary.funding_need_title}
                                    </Typography>

                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: '14px',
                                        mt: 1,
                                        textAlign: 'center',
                                      }}
                                    >
                                      {summary.total_applications} application
                                      {summary.total_applications !== 1
                                        ? 's'
                                        : ''}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 3,
                                        mt: 2,
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.draft_count || 0} Draft
                                          {summary.draft_count !== 1 ? 's' : ''}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.submitted_count || 0}{' '}
                                          Submitted
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.won_count || 0} Successful
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.lost_count || 0} Unsuccessful
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          fontSize: '14px',
                                          mt: 2,
                                          textAlign: 'center',
                                        }}
                                      >
                                        Funds Sorted:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          fontSize: '14px',
                                          textAlign: 'center',
                                        }}
                                      >
                                        ${summary.total_funds_recieved || 0} / $
                                        {summary.funding_need_amount || 0}
                                      </Typography>
                                    </Box>
                                  </Box>
                                }
                              />
                              <Button
                                component={Link}
                                to={`/applications/${summary.need_id}`}
                                variant="contained"
                                disableRipple
                                disabled={summary.total_applications === 0}
                                sx={{
                                  borderRadius: 2,
                                  backgroundColor: '#1C3045',
                                  m: 1,
                                  minWidth: '200px',
                                  maxWidth: '250px',
                                  textTransform: 'none',
                                  '&.Mui-disabled': { color: '#666666' },
                                }}
                              >
                                {summary.total_applications > 0
                                  ? 'View Applications'
                                  : 'No Applications yet'}
                              </Button>
                            </ListItem>
                          </Box>
                        )
                      })}
                      <Divider />
                    </List>
                  ) : (
                    <TableContainer
                      sx={{
                        width: '100%',
                        bgcolor: 'background.paper',
                        maxHeight: 600,
                        overflow: 'auto',
                      }}
                    >
                      <Table sx={{ width: '100%' }}>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#1C3045',
                                width: '35%',
                                minWidth: '200px',
                              }}
                            >
                              Funding Request
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#1C3045',
                                width: '15%',
                              }}
                            >
                              Total Applications
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#1C3045',
                                width: '20%',
                                minWidth: '150px',
                              }}
                            >
                              Application Status
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#1C3045',
                                width: '15%',
                              }}
                            >
                              Funds Sorted
                            </TableCell>
                            <TableCell
                              aria-label="Actions"
                              sx={{
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#1C3045',
                              }}
                            ></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeApplicationSummaries.map((summary) => {
                            return (
                              <TableRow
                                key={summary.need_id}
                                sx={{
                                  '&:last-child td, &:last-child th': {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell sx={{ pr: 10 }}>
                                  <Typography
                                    component={Link}
                                    to={`/match/${summary.need_id}`}
                                    variant="body1"
                                    sx={{
                                      fontSize: '16px',
                                      textDecoration: 'none',
                                      color: 'inherit',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      },
                                    }}
                                  >
                                    {summary.funding_need_title}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: '14px',
                                    }}
                                  >
                                    {summary.total_applications} application
                                    {summary.total_applications !== 1
                                      ? 's'
                                      : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 3,
                                    }}
                                  >
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: '14px' }}
                                      >
                                        {summary.draft_count || 0} Draft
                                        {summary.draft_count !== 1 ? 's' : ''}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: '14px' }}
                                      >
                                        {summary.submitted_count || 0} Submitted
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: '14px' }}
                                      >
                                        {summary.won_count || 0} Successful
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontSize: '14px' }}
                                      >
                                        {summary.lost_count || 0} Unsuccessful
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: '14px',
                                    }}
                                  >
                                    ${summary.total_funds_recieved || 0} / $
                                    {summary.funding_need_amount || 0}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    component={Link}
                                    to={`/applications/${summary.need_id}`}
                                    variant="contained"
                                    disableRipple
                                    disabled={summary.total_applications === 0}
                                    sx={{
                                      borderRadius: 2,
                                      backgroundColor: '#1C3045',
                                      minWidth: '150px',
                                      textTransform: 'none',
                                      '&.Mui-disabled': { color: '#666666' },
                                    }}
                                  >
                                    {summary.total_applications > 0
                                      ? 'View Applications'
                                      : 'No Applications yet'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      width: '60vw',
                      px: 6,
                      mt: 5,
                      height: '20vh',
                      border: 1,
                      borderColor: '#1C3045',
                      borderRadius: 2,
                      mx: 'auto',
                      bgcolor: '#f7f7f7',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        mt: 2,
                        mb: 1,
                        fontSize: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {' '}
                      No current funding requests with applications.
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        mb: 1,
                        textAlign: 'center',
                        fontSize: '16px',
                      }}
                    >
                      {' '}
                      You can generate applications through the{' '}
                      <Link to="/match" style={{ color: '#1C3045' }}>
                        Match page
                      </Link>{' '}
                      in the View Results section.
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        mb: 1,
                        textAlign: 'center',
                        fontSize: '14px',
                        mt: 2,
                        maxWidth: '80%',
                        mx: 'auto',
                        fontStyle: 'italic',
                      }}
                    >
                      If you've already generated applications, please check if
                      any of the funding requests have been archived. <br />
                      Use the 'Show Archived' button to view archived funding
                      requests.
                    </Typography>
                  </Box>
                )}
              </Box>
              {showArchived && (
                <Box sx={{ width: '100%', mt: 5 }}>
                  <Typography
                    variant="h6"
                    component={'h4'}
                    sx={{
                      mb: 1,
                      mt: 2,
                      textAlign: 'left',
                      pt: 3,
                    }}
                  >
                    Archived Funding Requests
                  </Typography>
                  {archivedApplicationSummaries.length > 0 ? (
                    isMobile ? (
                      archivedApplicationSummaries.map((summary) => {
                        return (
                          <Box
                            key={summary.need_id}
                            sx={{
                              width: '100%',
                            }}
                          >
                            <Divider />
                            <ListItem
                              sx={{
                                py: 1,
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      my: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: '16px',
                                        textAlign: 'center',
                                        mb: 1,
                                      }}
                                    >
                                      {summary.funding_need_title}
                                    </Typography>

                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: '14px',
                                        mt: 1,
                                        textAlign: 'center',
                                      }}
                                    >
                                      {summary.total_applications} application
                                      {summary.total_applications !== 1
                                        ? 's'
                                        : ''}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 3,
                                        mt: 2,
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.draft_count || 0} Draft
                                          {summary.draft_count !== 1 ? 's' : ''}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.submitted_count || 0}{' '}
                                          Submitted
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.won_count || 0} Successful
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.lost_count || 0} Unsuccessful
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          fontSize: '14px',
                                          mt: 2,
                                          textAlign: 'center',
                                        }}
                                      >
                                        Funds Sorted:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                          fontSize: '14px',
                                          textAlign: 'center',
                                        }}
                                      >
                                        ${summary.total_funds_recieved || 0} / $
                                        {summary.funding_need_amount || 0}
                                      </Typography>
                                    </Box>
                                  </Box>
                                }
                              />
                              <Button
                                component={Link}
                                to={`/applications/${summary.need_id}`}
                                variant="contained"
                                disableRipple
                                sx={{
                                  borderRadius: 2,
                                  backgroundColor: '#1C3045',
                                  m: 1,
                                  minWidth: '200px',
                                  maxWidth: '250px',
                                  textTransform: 'none',
                                }}
                              >
                                View Applications
                              </Button>
                            </ListItem>
                          </Box>
                        )
                      })
                    ) : (
                      <TableContainer
                        sx={{
                          width: '100%',
                          bgcolor: 'background.paper',
                          maxHeight: 600,
                          overflow: 'auto',
                        }}
                      >
                        <Table sx={{ width: '100%' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#1C3045',
                                  width: '35%',
                                  minWidth: '200px',
                                }}
                              >
                                Funding Request
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#1C3045',
                                  width: '15%',
                                }}
                              >
                                Total Applications
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#1C3045',
                                  width: '20%',
                                  minWidth: '150px',
                                }}
                              >
                                Application Status
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#1C3045',
                                  width: '15%',
                                }}
                              >
                                Funds Sorted
                              </TableCell>
                              <TableCell
                                aria-label="Actions"
                                sx={{
                                  fontSize: '16px',
                                  fontWeight: 600,
                                  color: '#1C3045',
                                }}
                              ></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {archivedApplicationSummaries.map((summary) => {
                              return (
                                <TableRow
                                  key={summary.need_id}
                                  sx={{
                                    '&:last-child td, &:last-child th': {
                                      border: 0,
                                    },
                                  }}
                                >
                                  <TableCell sx={{ pr: 10 }}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: '16px',
                                      }}
                                    >
                                      {summary.funding_need_title}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: '14px',
                                      }}
                                    >
                                      {summary.total_applications} application
                                      {summary.total_applications !== 1
                                        ? 's'
                                        : ''}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 3,
                                      }}
                                    >
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.draft_count || 0} Draft
                                          {summary.draft_count !== 1 ? 's' : ''}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.submitted_count || 0}{' '}
                                          Submitted
                                        </Typography>
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.won_count || 0} Successful
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{ fontSize: '14px' }}
                                        >
                                          {summary.lost_count || 0} Unsuccessful
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        fontSize: '14px',
                                      }}
                                    >
                                      ${summary.total_funds_recieved || 0} / $
                                      {summary.funding_need_amount || 0}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      component={Link}
                                      to={`/applications/${summary.need_id}`}
                                      variant="contained"
                                      disableRipple
                                      sx={{
                                        borderRadius: 2,
                                        backgroundColor: '#1C3045',
                                        minWidth: '150px',
                                        textTransform: 'none',
                                      }}
                                    >
                                      View Applications
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{
                        mt: 2,
                        mb: 1,
                        fontSize: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {' '}
                      No archived funding requests with applications.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </>
      </Authenticated>
    </>
  )
}
